import { defineComponent as _defineComponent } from 'vue'
import FeaturedCategoriesPageTitle from './FeaturedCategoriesPageTitle.vue';
import Icon from 'JS/components/Icon.vue';
import LinkButton from 'JS/components/LinkButton.vue';
import { computed, nextTick, ref } from 'vue';
import { createPromoDataAttributes } from 'JS/utilities/analytics';
import { cssVarsToStyleString } from 'JS/dxp/utilities/css-vars';
import { FeaturedCategoriesWidgetItemData, FeaturedCategoriesWidgetResource } from 'JS/dxp/types/widgets';
import { first, range } from 'JS/utilities/iterator';
import { IconName } from 'JS/constants/icons';
import { pipe } from 'JS/utilities/fp';
import { useDxpPageTitle } from 'JS/dxp/composables/title';
import { useUuid } from 'JS/composables/uuid';


export default /*#__PURE__*/_defineComponent({
  __name: 'FeaturedCategoriesWidget',
  props: {
    widget: null
  },
  setup(__props: any) {

const props = __props as {
	widget: FeaturedCategoriesWidgetResource;
}



const hiddenItemClassName = 'featured-categories-widget-item--hidden-mobile';
const mobileInitialShown = 6;

const id = useUuid();

const root = ref<HTMLElement>();
const showAll = ref(false);
const showButtonIcon = computed<IconName>(() => showAll.value ? 'chevron-up' : 'chevron-down');

const shouldActAsPageHeader = computed(() => props.widget.data.shouldActAsPageHeader);
const pageTitle = useDxpPageTitle();

const mobileLayout = computed(() => shouldActAsPageHeader.value ? 'stacked' : 'carousel');

function toggleShowAll() {
	if (showAll.value) {
		showAll.value = false;
	} else {
		const firstHidden = root.value?.querySelector<HTMLElement>(`.${hiddenItemClassName}`);
		showAll.value = true;
		nextTick(() => firstHidden?.focus());
	}
}

const columns = computed(() => {
	const items = props.widget.data.items.length;
	return ({
		mobile: 2,
		tablet: findIdealNumberOfColumns({ min: 3, max: 4, items }),
		desktop: findIdealNumberOfColumns({ min: 5, max: 7, items }),
		desktopLarge: findIdealNumberOfColumns({ min: 7, max: 10, items }),
	});
});

/**
 * Determines the largest number of columns between min and max which produce a grid with > 1 item
 * in the final row. If no value in the range does, then it opts to maximize the number of columns.
 */
function findIdealNumberOfColumns({ min, max, items }: { min: number; max: number; items: number; }) {
	if (items <= max) {
		return items;
	}

	return pipe(
		range(max, min),
		first(columns => items % columns !== 1),
	) ?? max;
}

/**
 * Given the number of columns, determine how many spaces (in increments of 0.5)
 * orphans must shift from their default grid position in order to be centered
 */
function getSpacesToShiftOrphans(columns: number) {
	const numItems = props.widget.data.items.length;
	const numOrphans = numItems % columns;

	if (numItems <= columns || numOrphans === 0) {
		return 0;
	}

	return (columns - numOrphans) / 2;
}

/**
 * Returns true if the index is in the last row and the last row's width
 * is less than the number of columns
 */
function isOrphan(index: number, columns: number) {
	const numItems = props.widget.data.items.length;
	const lastIndexOfEvenRow = numItems - (numItems % columns) - 1;
	return index > lastIndexOfEvenRow;
}

/**
 * Gets the amount we need to shift a specific item. Only orphaned
 * items will shift @see {@link getSpacesToShiftOrphans}. If the
 * item is not an orphan, return undefined to reduce the size of the
 * generated markup
 */
function getSpacesToShiftItem(index: number, columns: number): number | undefined {
	return isOrphan(index, columns)
		? getSpacesToShiftOrphans(columns)
		: undefined;
}

const gaDataAttributes = computed(() => createPromoDataAttributes({
	promoId: `${props.widget.data.shouldActAsPageHeader ? pageTitle.value : props.widget.data.title}`, //Same as promo ID in this instance since there are no
	creativeSlot: "dxp_featured_categories_0",
	creativeName: `dxp_featured_categories_${props.widget.data.shouldActAsPageHeader ? 'header' : 'not_header'}`,
	promoName: `${props.widget.data.shouldActAsPageHeader ? pageTitle.value : props.widget.data.title}`
}));

function getClickDataAttributes(item: FeaturedCategoriesWidgetItemData, idx: number) {
	return createPromoDataAttributes({
		promoId: item.link.replace("https://www.farmandfleet.com", ""),
		creativeSlot: `dxp_featured_categories_${idx + 1}`,
		creativeName: `dxp_featured_categories_${props.widget.data.shouldActAsPageHeader ? 'header' : 'not_header'}`,
		promoName: `${item.label}`
	});
}


return { __sfc: true,props, hiddenItemClassName, mobileInitialShown, id, root, showAll, showButtonIcon, shouldActAsPageHeader, pageTitle, mobileLayout, toggleShowAll, columns, findIdealNumberOfColumns, getSpacesToShiftOrphans, isOrphan, getSpacesToShiftItem, gaDataAttributes, getClickDataAttributes, FeaturedCategoriesPageTitle, Icon, LinkButton, cssVarsToStyleString }
}

})